import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { History } from 'history';
import { isSessionExpired, handleExpiredSession } from 'utils/session';
import { loadAuthToken, loadSessionId } from 'utils/tokenStorage';
import { hasRequiredScope } from 'utils/scope';
import { routes } from 'utils/routeHelper';
import { Config } from '../utils/config/config';
import { VERSION_NUMBER } from '../utils/constants';
import { WeaverApiClientInstance } from '../api/weaverApiClientInstance';

interface IConnectAuthorization {
  requiredScope?: string;
}

interface IStateProps {
  isLoggedIn: boolean;
  currentUserScopes: string[];
}

interface IOwnProps extends IConnectAuthorization {
  children: JSX.Element[] | JSX.Element;
  history: History;
}

export type AuthorizationRouteType = IStateProps & IOwnProps;

export const shouldRedirectToLogin = (isLoggedIn: boolean) => {
  const isExpired = isSessionExpired();
  handleExpiredSession(isExpired);
  return !isLoggedIn || isExpired;
};

export const hasPermission = (
  currentUserScopes: string[],
  requiredScope?: string,
) => {
  if (
    requiredScope &&
    hasRequiredScope(currentUserScopes)(requiredScope) === false
  ) {
    return false;
  }
  return true;
};

const initializePendo = async () => {
  // @ts-ignore
  if (typeof pendo === 'undefined') return;
  const {
    data: { id },
  } = await WeaverApiClientInstance.getUserProfile();
  const offset = new Date().getTimezoneOffset();
  // @ts-ignore
  pendo.initialize({
    visitor: {
      // ID is typed as optional, so providing a fallback in case that comes in falsy.
      id: id,
      sessionId: loadSessionId(),
      staffId: id,
      timezoneOffset: `${offset}`,
    },
    account: {
      id: Config?.BUILD_ENV.toUpperCase() ?? '',
      appVersion: VERSION_NUMBER,
    },
  });
};

export class AuthorizationRouteBase extends Component<AuthorizationRouteType> {
  ensureAuthAndValidSession = (props: AuthorizationRouteType) => {
    const { isLoggedIn, history, requiredScope, currentUserScopes } = props;
    if (shouldRedirectToLogin(isLoggedIn)) {
      history.push(routes.LOG_IN);
    } else if (!hasPermission(currentUserScopes, requiredScope)) {
      history.push(routes.INVALID_PATH);
    }
  };

  componentDidUpdate() {
    this.ensureAuthAndValidSession(this.props);
  }

  componentDidMount() {
    this.ensureAuthAndValidSession(this.props);
    initializePendo();
  }

  render() {
    const { isLoggedIn, children, currentUserScopes, requiredScope } =
      this.props;
    if (shouldRedirectToLogin(isLoggedIn)) {
      return <Redirect to={routes.LOG_IN} />;
    }
    if (hasPermission(currentUserScopes, requiredScope)) {
      // Pendo needs to be initialized after login since it is passing along UserProfile information.
      return <>{children}</>;
    }
    return <Redirect to={routes.INVALID_PATH} />;
  }
}

const mapStateToProps = ({
  authenticate,
}: {
  authenticate: IAuthenticateState;
}) => ({
  isLoggedIn: Boolean(authenticate.token || loadAuthToken()),
  currentUserScopes: authenticate.profile?.scopes ?? [],
});

export const AuthorizationRouteConnected = connect(mapStateToProps)(
  AuthorizationRouteBase,
);

const connectAuthorization =
  (WrappedComponent: any, requireRoleProps: IConnectAuthorization = {}): any =>
  // eslint-disable-next-line
  (props: AuthorizationRouteType) =>
    (
      <AuthorizationRouteConnected
        {...requireRoleProps}
        history={props.history}
      >
        <WrappedComponent {...props} />
      </AuthorizationRouteConnected>
    );

export default connectAuthorization;
